import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../images/logo.png";

const Login = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  // Demo users: only these five 9-digit numeric User IDs are allowed to login
  const allowedUsers = [
  { userId: "738492516", password: "lkjK$%4Q@9", name: "738492516" },
  { userId: "904175283", password: "Zx!7mP#2L$", name: "904175283" },
  { userId: "561804972", password: "A9$kW@%r8Q", name: "561804972" },
  { userId: "289746105", password: "P@4%Xl9!K$", name: "289746105" },
  { userId: "670915834", password: "mQ$!7Zk@4%", name: "670915834" },
  { userId: "agent1", password: "mQ$!7Zk@4%", name: "670915834" },
    
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation
    if (!userId || !password) {
      setError("Please enter both User ID and password");
      setLoading(false);
      return;
    }

    // User ID must be exactly 9 digits (numeric only)
    const userIdPattern = /^\d{9}$/;
    if (!userIdPattern.test(userId)) {
      setError("User ID must be a 9-digit number (digits only)");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      // Simulate API call to backend
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock authentication - in production, verify with backend
      const matchedUser = allowedUsers.find((u) => u.userId === userId && u.password === password);

      if (matchedUser) {
        const userData = {
          id: matchedUser.userId,
          userId: matchedUser.userId,
          email: `${matchedUser.userId}@melbetsupport.com`,
          name: matchedUser.name,
        };

        // Save auth data to localStorage
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("authToken", "mock-token-" + Date.now());

        // Update auth context
        login(userData);

        // Redirect to payment page
        navigate("/payment");
      } else {
        setError("Invalid credentials. ");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1f1f1e 0%, #353331 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: { xs: 1, sm: 2 },
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xs" sx={{ maxWidth: { xs: "100%", sm: "500px" }, width: "100%" }}>
        <Card
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: "16px",
            boxShadow: "0 25px 70px rgba(0, 0, 0, 0.5)",
            background: "linear-gradient(135deg,  rgb(188, 149, 34) 100%)",
            maxWidth: "100%",
            width: "100%",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Logo */}
          <Box sx={{ textAlign: "center", mb: 3,}}>
            <Box
              component="img"
              src={logo}
              alt="melbet"
              sx={{ height: { xs: 35, sm: 40 }, mb: 2, cursor: "pointer" ,backgroundColor:'black',borderRadius:'8px',padding:'4px 8px'}}
            />
          </Box>

          {/* Header */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
             color: "#000000",
              textAlign: "center",
              mb: 1,
        
              fontSize: { xs: "1.75rem", sm: "2.125rem" },
            }}
          >
            Agent Portal
          </Typography>

          <Typography
            sx={{
             color: "#000000",
              textAlign: "center",
              fontSize: { xs: "0.9rem", sm: "0.95rem" },
              mb: 3,
              fontWeight: 500,
            }}
          >
            Sign in to your account to manage deposits and withdrawals
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "8px", background: "#ffcdd2", color: "#c62828", border: "1px solid #ef5350" }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin} sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            {/* User ID Field */}
            <TextField
              label="User ID"
              type="text"
              placeholder="Enter your user ID"
              fullWidth
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  "& fieldset": {
                    borderColor: "rgba(212, 160, 23, 0.5)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d4a017",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#d4a017",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  color: "#8b6914",
                  padding: { xs: "10px 12px", sm: "12px 14px" },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  "&.Mui-focused": {
                    color: "#1e5a96",
                  },
                },
              }}
            />

            {/* Password Field */}
            <TextField
              label="Password"
              type="password"
              placeholder="••••••••"
              fullWidth
              value={password}
              onChange={(e) => 
                setPassword(e.target.value)}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  "& fieldset": {
                    borderColor: "rgba(212, 160, 23, 0.5)",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d4a017",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#d4a017",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  color: "#8b6914",
                  padding: { xs: "10px 12px", sm: "12px 14px" },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  "&.Mui-focused": {
                    color: "#b8860b",
                  },
                },
              }}
            />
            {/* Login Button */}
            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, rgb(254, 189, 2) 0%, rgb(124, 95, 10) 100%)",
                color: "#000000",
                fontWeight: 700,
                py: { xs: 1.2, sm: 1.3 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
                borderRadius: "10px",
                transition: "all 0.3s ease",
                position: "relative",
                boxShadow: "0 8px 20px rgba(254, 189, 2, 0.3)",
                "&:hover": !loading && {
                  background: "linear-gradient(135deg, rgb(254, 189, 2) 0%, black 100%)",
                  boxShadow: "0 12px 28px rgba(254, 189, 2, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  opacity: 0.8,
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                  <span>Logging in...</span>
                </Box>
              ) : (
                "Login"
              )}
            </Button>
          </Box>

          {/* Demo Credentials Info */}
          {/* <Box
            sx={{
              background: "#fff9e6",
              border: "2px solid #d4a017",
              borderRadius: "8px",
              p: 2,
              mt: 2.5,
            }}
          >
            <Typography
              sx={{
                color: "#8b6914",
                fontWeight: 600,
                fontSize: "0.9rem",
                mb: 0.8,
              }}
            >
              📝 Demo Login Credentials (only these will work):
            </Typography>
            {allowedUsers.map((u) => (
              <Typography key={u.userId} sx={{ color: "#666", fontSize: "0.85rem", mb: 0.5 }}>
                User ID: <strong>{u.userId}</strong> — Password: <strong>{u.password}</strong>
              </Typography>
            ))}
          </Box> */}

          {/* Footer Info */}
          <Typography
            sx={{
              color: "#000000",
              textAlign: "center",
              fontSize: "0.8rem",
              mt: 3,
            }}
          >
            Secure login with User ID and password.
          </Typography>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
